import {SimpleListView} from '@github-ui/simple-list-view'
import {SimpleListHeader} from '@github-ui/simple-list-view/SimpleListHeader'
import {SimpleListItem} from '@github-ui/simple-list-view/SimpleListItem'
import {KebabHorizontalIcon, type Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {clsx} from 'clsx'
import React, {createContext, use, type ReactElement} from 'react'

import {useFocusVisibleWithin} from '../hooks/use-focus-visible-within'
import styles from './DashboardListView.module.css'

export function DashboardListViewItemsSkeletonLoader() {
  return (
    <>
      <li className={styles.skeletonItem}>
        <SkeletonText lines={2} />
      </li>
      <li className={styles.skeletonItem}>
        <SkeletonText lines={2} />
      </li>
      <li className={styles.skeletonItem}>
        <SkeletonText lines={2} />
      </li>
    </>
  )
}
export interface DashboardSimpleListViewProps {
  children: React.ReactNode
  className?: string
  'data-testid'?: string
}

export const DashboardListView = ({className, children, ...props}: DashboardSimpleListViewProps) => {
  const listRef = useFocusVisibleWithin()
  return (
    <div ref={listRef} className={clsx(styles.List, className)}>
      <SimpleListView {...props}>{children}</SimpleListView>
    </div>
  )
}

const SimpleListHeaderOverride = ({children}: {children: React.ReactNode}) => {
  return <SimpleListHeader className={styles.Header}>{children}</SimpleListHeader>
}

const SimpleListItemsOverride = ({children}: {children: React.ReactNode}) => {
  return <SimpleListView.Items className={styles.Items}>{children}</SimpleListView.Items>
}

const SimpleListItemOverride = ({children, className}: {children: React.ReactNode; className?: string}) => {
  return <SimpleListItem className={clsx(styles.Item, className)}>{children}</SimpleListItem>
}

const SimpleListItemLeadingVisualOverride = ({children, ...props}: {children: React.ReactNode}) => {
  return (
    <SimpleListItem.LeadingVisual className={styles.ItemLeadingVisual} {...props}>
      {children}
    </SimpleListItem.LeadingVisual>
  )
}

// Create custom link title to allow entire list item row to be clickable
const SimpleListItemTitleOverride = ({
  children,
  className,
  href,
  as = 'a',
  onClick,
}: {
  children: React.ReactNode
  className?: string
  href?: string
  as?: React.ElementType
  onClick?: (e?: React.MouseEvent) => void
}) => {
  const Component = as
  return (
    <Component href={href} className={clsx(styles.ItemTitle, className)} onClick={onClick}>
      <SimpleListItem.Title className={styles.ItemTitleInner}>{children}</SimpleListItem.Title>
    </Component>
  )
}

const SimpleListItemActionsOverride = ({children}: {children: React.ReactNode}) => {
  return (
    <SimpleListItem.Actions className={styles.ItemActions} actionStyling={{gap: 'normal'}}>
      {children}
    </SimpleListItem.Actions>
  )
}

const SimpleListItemDescriptionOverride = ({children}: {children: React.ReactNode}) => {
  return <SimpleListItem.Description className={styles.ItemDescription}>{children}</SimpleListItem.Description>
}

const SimpleListItemStatusOverride = ({
  children,
}: {
  children: ReactElement<typeof Label> | Array<ReactElement<typeof Label>>
}) => {
  return <SimpleListItem.Status>{children}</SimpleListItem.Status>
}

type MenuContextType = 'actionList' | 'contextBubble'

export const MenuContext = createContext<MenuContextType>('actionList')

const ListItemTrailingActionsMenu = ({
  children,
  'aria-label': ariaLabel,
  anchorRef,
}: {
  children: React.ReactNode
  'aria-label': string
  anchorRef?: React.RefObject<HTMLButtonElement | null>
}) => {
  return (
    <>
      <div className={styles.touchOnly}>
        <SimpleListItem.TrailingActions className={styles.ItemTrailingActions}>
          <MenuContext value="actionList">
            <ActionMenu anchorRef={anchorRef}>
              <ActionMenu.Anchor>
                <IconButton icon={KebabHorizontalIcon} aria-label={ariaLabel} variant="invisible" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>{children}</ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </MenuContext>
        </SimpleListItem.TrailingActions>
      </div>

      <div className={styles.hoverOnly}>
        <div className={styles.contextBubbleMenu}>
          <MenuContext value="contextBubble">{children}</MenuContext>
        </div>
      </div>
    </>
  )
}

type ListItemTrailingActionsMenuItemProps = {
  icon: Icon
  label: string
  href?: string
  onClick?: () => void
}

const ListItemTrailingActionsMenuItem = ({icon: Icon, label, href, onClick}: ListItemTrailingActionsMenuItemProps) => {
  const menu = use(MenuContext)

  if (menu === 'contextBubble') {
    return (
      <IconButton
        variant="invisible"
        icon={Icon}
        className={styles.contextBubbleMenuItem}
        tooltipDirection="nw"
        aria-label={label}
        onClick={onClick}
        {...(href ? {href, as: 'a'} : {})}
      />
    )
  }

  return href ? (
    <ActionList.LinkItem href={href} onSelect={onClick}>
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.LinkItem>
  ) : (
    <ActionList.Item onSelect={onClick}>
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.Item>
  )
}

// Named exports for SimpleListView subcomponents (new API)
export const DashboardListViewHeader = SimpleListHeaderOverride
export const DashboardListViewHeaderTitle = SimpleListHeader.Title
export const DashboardListViewHeaderMetadata = SimpleListHeader.Metadata
export const DashboardListViewItems = SimpleListItemsOverride

// SimpleListItem exports for new usage
export const DashboardListItem = SimpleListItemOverride
export const DashboardListItemLeadingVisual = SimpleListItemLeadingVisualOverride
export const DashboardListItemTitle = SimpleListItemTitleOverride
export const DashboardListItemStatus = SimpleListItemStatusOverride
export const DashboardListItemDescription = SimpleListItemDescriptionOverride
export const DashboardListItemActions = SimpleListItemActionsOverride
export const DashboardListItemTrailingActionsMenu = Object.assign(ListItemTrailingActionsMenu, {
  Item: ListItemTrailingActionsMenuItem,
})
